import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useDialog } from "@/lib/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Trash2, Pencil, Plus, Save, X, Sparkles, Wrench, Zap, FileText } from "lucide-react";

const CAT_META = {
  new: { label: "🆕 Nuevo", Icon: Sparkles, cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" },
  fix: { label: "🔧 Arreglo", Icon: Wrench, cls: "bg-sky-500/15 text-sky-300 border-sky-500/40" },
  improvement: { label: "⚡ Mejora", Icon: Zap, cls: "bg-amber-500/15 text-amber-300 border-amber-500/40" },
  legal: { label: "📄 Legal", Icon: FileText, cls: "bg-purple-500/15 text-purple-300 border-purple-500/40" },
};

const emptyDraft = () => ({ title: "", description: "", category: "improvement", published_at: "" });

const fmt = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("es-EC", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

/**
 * Sección "Novedades" del panel admin. Formulario sencillo (título, descripción,
 * categoría) + lista con edit/delete. Fecha/hora automática al crear (el admin
 * puede sobrescribirla si quiere fechar retroactivamente algo que ya sucedió).
 */
export default function AdminChangelog() {
  const { confirm } = useDialog();
  const [rows, setRows] = useState(null);
  const [draft, setDraft] = useState(emptyDraft());
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/changelog");
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo cargar el listado");
      setRows([]);
    }
  };
  useEffect(() => { load(); }, []);

  const startEdit = (row) => {
    setEditingId(row.id);
    setDraft({
      title: row.title || "",
      description: row.description || "",
      category: row.category || "improvement",
      published_at: row.published_at ? row.published_at.slice(0, 16) : "",
    });
  };
  const cancelEdit = () => { setEditingId(null); setDraft(emptyDraft()); };

  const submit = async (e) => {
    e.preventDefault();
    if (!draft.title.trim() || !draft.description.trim()) return toast.error("Título y descripción son obligatorios");
    setBusy(true);
    try {
      const payload = {
        title: draft.title.trim(),
        description: draft.description.trim(),
        category: draft.category,
      };
      if (draft.published_at) payload.published_at = new Date(draft.published_at).toISOString();
      if (editingId) {
        await api.put(`/admin/changelog/${editingId}`, payload);
        toast.success("Entrada actualizada");
      } else {
        await api.post("/admin/changelog", payload);
        toast.success("Novedad publicada");
      }
      cancelEdit();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo guardar");
    } finally { setBusy(false); }
  };

  const remove = async (row) => {
    const ok = await confirm({
      title: "¿Eliminar novedad?",
      description: `"${row.title}" se eliminará del apartado público.`,
      confirmText: "Eliminar",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/admin/changelog/${row.id}`);
      toast.success("Novedad eliminada");
      if (editingId === row.id) cancelEdit();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo eliminar");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-changelog-page">
      <div>
        <h2 className="font-display text-2xl">Novedades / Changelog</h2>
        <p className="text-sm text-neutral-500 mt-1">
          Publica aquí las mejoras, arreglos y funciones nuevas de HitTCG. Aparecen en <b>/novedades</b> y en el footer, visibles sin cuenta.
        </p>
      </div>

      <form onSubmit={submit} className="bg-[#111] border border-[#262626] rounded-md p-4 sm:p-6 space-y-4" data-testid="admin-changelog-form">
        <div className="flex items-center justify-between gap-2">
          <div className="font-medium text-white text-sm sm:text-base">
            {editingId ? "Editando entrada" : "Nueva entrada"}
          </div>
          {editingId && (
            <Button type="button" size="sm" variant="ghost" onClick={cancelEdit} className="text-neutral-400 h-8" data-testid="admin-changelog-cancel">
              <X size={14} className="mr-1" /> Cancelar edición
            </Button>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-neutral-400">Título *</Label>
            <Input
              data-testid="admin-changelog-title"
              value={draft.title}
              onChange={(e) => setDraft(d => ({ ...d, title: e.target.value }))}
              placeholder="Ej. Buscador más flexible"
              maxLength={140}
              required
              className="bg-[#171717] border-[#262626]"
            />
          </div>
          <div>
            <Label className="text-xs text-neutral-400">Categoría *</Label>
            <Select value={draft.category} onValueChange={(v) => setDraft(d => ({ ...d, category: v }))}>
              <SelectTrigger data-testid="admin-changelog-category" className="bg-[#171717] border-[#262626]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(CAT_META).map(([k, m]) => (
                  <SelectItem key={k} value={k}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label className="text-xs text-neutral-400">Descripción *</Label>
          <Textarea
            data-testid="admin-changelog-description"
            value={draft.description}
            onChange={(e) => setDraft(d => ({ ...d, description: e.target.value }))}
            placeholder="Describe brevemente el cambio que verá el usuario."
            rows={3}
            required
            className="bg-[#171717] border-[#262626]"
          />
        </div>
        <div>
          <Label className="text-xs text-neutral-400">Fecha y hora (opcional — se usa la actual si lo dejas vacío)</Label>
          <Input
            data-testid="admin-changelog-published-at"
            type="datetime-local"
            value={draft.published_at}
            onChange={(e) => setDraft(d => ({ ...d, published_at: e.target.value }))}
            className="bg-[#171717] border-[#262626]"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button type="submit" disabled={busy} data-testid="admin-changelog-submit" className="bg-[#EF4444] hover:bg-[#DC2626]">
            {editingId ? <><Save size={14} className="mr-1" /> Guardar cambios</> : <><Plus size={14} className="mr-1" /> Publicar novedad</>}
          </Button>
        </div>
      </form>

      <div className="bg-[#111] border border-[#262626] rounded-md" data-testid="admin-changelog-list">
        <div className="p-4 border-b border-[#262626] font-medium">Publicadas ({rows?.length ?? 0})</div>
        {rows === null ? (
          <div className="p-6 text-sm text-neutral-500">Cargando…</div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-sm text-neutral-500">Aún no hay novedades publicadas.</div>
        ) : (
          <ul className="divide-y divide-[#1f1f1f]">
            {rows.map((r) => {
              const meta = CAT_META[r.category] || CAT_META.improvement;
              return (
                <li key={r.id} data-testid={`admin-changelog-row-${r.id}`} className="p-4 flex flex-col sm:flex-row sm:items-start sm:gap-4 gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded border uppercase tracking-wider ${meta.cls}`}>
                        {meta.label}
                      </span>
                      <span className="text-[10px] text-neutral-500 font-mono">{fmt(r.published_at)}</span>
                    </div>
                    <div className="font-medium text-white mt-1">{r.title}</div>
                    <div className="text-sm text-neutral-400 mt-1 line-clamp-3 whitespace-pre-line">{r.description}</div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button type="button" size="sm" variant="ghost" onClick={() => startEdit(r)} data-testid={`admin-changelog-edit-${r.id}`} className="text-neutral-400 hover:text-white h-8 px-2">
                      <Pencil size={14} />
                    </Button>
                    <Button type="button" size="sm" variant="ghost" onClick={() => remove(r)} data-testid={`admin-changelog-delete-${r.id}`} className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8 px-2">
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
